import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    CheckCircle,
    XCircle,
    Clock,
    Filter,
    Search,
    Loader2,
    ChevronDown,
    ChevronUp,
    AlertCircle,
} from 'lucide-react';
import type { ApiResponse, TimeEntry as TimeEntryType } from '../types';
import { hoursToDays } from '../lib/utils';

interface PendingApproval extends TimeEntryType {
    userName: string;
    userEmail: string;
    projectName: string;
    projectCode: string;
}

export function Approvals() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [projectFilter, setProjectFilter] = useState('');
    const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
    const [rejectingEntry, setRejectingEntry] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');

    const { data: pendingData, isLoading } = useQuery({
        queryKey: ['time-entries', 'pending'],
        queryFn: () =>
            api.get<ApiResponse<PendingApproval[]>>('/api/time-entries/pending/approvals'),
    });

    const pendingEntries = pendingData?.data || [];

    // Filter entries
    const filteredEntries = pendingEntries.filter((entry) => {
        const searchLower = search.toLowerCase();
        const matchesSearch =
            !search ||
            entry.userName.toLowerCase().includes(searchLower) ||
            entry.projectCode.toLowerCase().includes(searchLower) ||
            entry.projectName.toLowerCase().includes(searchLower);

        const matchesProject = !projectFilter || entry.projectId === projectFilter;

        return matchesSearch && matchesProject;
    });

    // Group by user
    const entriesByUser = filteredEntries.reduce((acc, entry) => {
        if (!acc[entry.userId]) {
            acc[entry.userId] = {
                userName: entry.userName,
                userEmail: entry.userEmail,
                entries: [],
                totalHours: 0,
            };
        }
        acc[entry.userId].entries.push(entry);
        acc[entry.userId].totalHours += entry.hours;
        return acc;
    }, {} as Record<string, { userName: string; userEmail: string; entries: PendingApproval[]; totalHours: number }>);

    // Get unique projects for filter
    const uniqueProjects = Array.from(
        new Map(pendingEntries.map((e) => [e.projectId, { id: e.projectId, name: e.projectName, code: e.projectCode }])).values()
    );

    // Mutations
    const approveMutation = useMutation({
        mutationFn: (entryId: string) =>
            api.post(`/api/time-entries/${entryId}/approve`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const rejectMutation = useMutation({
        mutationFn: ({ entryId, reason }: { entryId: string; reason: string }) =>
            api.post(`/api/time-entries/${entryId}/reject`, { reason }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
            setRejectingEntry(null);
            setRejectionReason('');
        },
    });

    const approveAllMutation = useMutation({
        mutationFn: (entryIds: string[]) =>
            api.post('/api/time-entries/batch/approve', { entryIds }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        },
    });

    const toggleExpanded = (userId: string) => {
        const newExpanded = new Set(expandedEntries);
        if (newExpanded.has(userId)) {
            newExpanded.delete(userId);
        } else {
            newExpanded.add(userId);
        }
        setExpandedEntries(newExpanded);
    };

    const handleReject = (entryId: string) => {
        if (!rejectionReason.trim()) {
            return;
        }
        rejectMutation.mutate({ entryId, reason: rejectionReason });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--color-text)]">Approbations</h1>
                    <p className="text-[var(--color-text-muted)]">
                        {filteredEntries.length} saisie{filteredEntries.length > 1 ? 's' : ''} en
                        attente
                    </p>
                </div>

                {filteredEntries.length > 0 && (
                    <button
                        onClick={() =>
                            approveAllMutation.mutate(filteredEntries.map((e) => e.id))
                        }
                        disabled={approveAllMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--color-success)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                        {approveAllMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <CheckCircle className="w-4 h-4" />
                        )}
                        Tout valider
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher par collaborateur ou projet..."
                        className="input pl-10"
                    />
                </div>

                {uniqueProjects.length > 1 && (
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
                        <select
                            value={projectFilter}
                            onChange={(e) => setProjectFilter(e.target.value)}
                            className="input pl-10 pr-8 appearance-none"
                        >
                            <option value="">Tous les projets</option>
                            {uniqueProjects.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.code} - {p.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Pending entries */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : filteredEntries.length === 0 ? (
                <div className="text-center py-12 card">
                    <CheckCircle className="w-12 h-12 text-[var(--color-success)] opacity-50 mx-auto mb-4" />
                    <p className="text-[var(--color-text-muted)]">Aucune saisie en attente d'approbation</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {Object.entries(entriesByUser).map(([userId, userData]) => (
                        <div
                            key={userId}
                            className="card !p-0 overflow-hidden"
                        >
                            {/* En-tête du collaborateur. « Tout valider » est un bouton voisin de
                                celui qui déplie, et non plus son enfant : un bouton dans un bouton
                                n'est pas du HTML valide, et le clic y dépendait de stopPropagation. */}
                            <div className="flex items-center justify-between hover:bg-[var(--color-bg)] transition-colors">
                                <button
                                    type="button"
                                    onClick={() => toggleExpanded(userId)}
                                    aria-expanded={expandedEntries.has(userId)}
                                    className="flex-1 flex items-center gap-3 p-4 text-left"
                                >
                                    <div className="w-10 h-10 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
                                        <span className="text-[var(--color-primary)] font-medium">
                                            {userData.userName
                                                .split(' ')
                                                .map((n) => n[0])
                                                .join('')
                                                .toUpperCase()
                                                .slice(0, 2)}
                                        </span>
                                    </div>
                                    <div className="text-left">
                                        <p className="font-medium text-[var(--color-text)]">{userData.userName}</p>
                                        <p className="text-sm text-[var(--color-text-muted)]">
                                            {userData.entries.length} saisie{userData.entries.length > 1 ? 's' : ''} •{' '}
                                            {hoursToDays(userData.totalHours)}j au total
                                        </p>
                                    </div>
                                </button>
                                <div className="flex items-center gap-2 pr-4">
                                    <button
                                        type="button"
                                        onClick={() => approveAllMutation.mutate(userData.entries.map((e) => e.id))}
                                        className="px-3 py-1.5 text-sm text-[var(--color-success)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                                    >
                                        Tout valider
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toggleExpanded(userId)}
                                        aria-label={expandedEntries.has(userId) ? 'Replier' : 'Déplier'}
                                        className="p-1 rounded-lg"
                                    >
                                        {expandedEntries.has(userId) ? (
                                            <ChevronUp className="w-5 h-5 text-[var(--color-text-muted)]" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-[var(--color-text-muted)]" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Entries list */}
                            {expandedEntries.has(userId) && (
                                <div className="border-t border-[var(--color-border)] divide-y divide-[var(--color-border)]">
                                    {userData.entries.map((entry) => (
                                        <div key={entry.id} className="p-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium text-[var(--color-text)]">
                                                            {entry.projectCode}
                                                        </span>
                                                        <span className="text-[var(--color-text-muted)]">-</span>
                                                        <span className="text-[var(--color-text-secondary)]">{entry.projectName}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-[var(--color-text-muted)]">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {format(new Date(entry.entryDate), 'EEEE d MMMM', {
                                                                locale: fr,
                                                            })}
                                                        </span>
                                                        {entry.taskType && (
                                                            <span className="px-2 py-0.5 bg-[var(--color-bg)] rounded text-xs">
                                                                {entry.taskType}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {entry.description && (
                                                        <p className="text-sm text-[var(--color-text-secondary)] mt-2">
                                                            {entry.description}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="text-right flex-shrink-0">
                                                    <span className="text-xl font-bold text-[var(--color-text)]">
                                                        {hoursToDays(entry.hours)}j
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            {rejectingEntry === entry.id ? (
                                                <div className="mt-4 p-3 bg-[var(--color-bg)] rounded-lg">
                                                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                                                        Motif du rejet *
                                                    </label>
                                                    <textarea
                                                        value={rejectionReason}
                                                        onChange={(e) => setRejectionReason(e.target.value)}
                                                        rows={2}
                                                        className="input text-sm"
                                                        placeholder="Expliquez la raison du rejet..."
                                                    />
                                                    <div className="flex justify-end gap-2 mt-2">
                                                        <button
                                                            onClick={() => {
                                                                setRejectingEntry(null);
                                                                setRejectionReason('');
                                                            }}
                                                            className="btn-secondary text-sm"
                                                        >
                                                            Annuler
                                                        </button>
                                                        <button
                                                            onClick={() => handleReject(entry.id)}
                                                            disabled={!rejectionReason.trim() || rejectMutation.isPending}
                                                            className="px-3 py-1.5 text-sm bg-[var(--color-error)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                                        >
                                                            Confirmer le rejet
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-end gap-2 mt-4">
                                                    <button
                                                        onClick={() => setRejectingEntry(entry.id)}
                                                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-[var(--color-error)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                        Rejeter
                                                    </button>
                                                    <button
                                                        onClick={() => approveMutation.mutate(entry.id)}
                                                        disabled={approveMutation.isPending}
                                                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[var(--color-success)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                                    >
                                                        <CheckCircle className="w-4 h-4" />
                                                        Valider
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Legend */}
            <div className="bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-[var(--color-warning)] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[var(--color-warning)]">
                        <p className="font-medium mb-1">Rappel</p>
                        <p>
                            Les saisies validées ne peuvent plus être modifiées. Vérifiez les jours
                            et descriptions avant de valider.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
